import { type Resource } from "@core/shared/types";
import { URLS, isRTL } from "@core/shared/utils";
import { BaseLink } from "@design-system/components/link";
import { trans } from "@mongez/localization";
import { TypewriterText } from "../../shared/components/TypewriterText";

interface HeroSectionProps {
  heroImage?: Resource;
  hero?: {
    title: string;
    text: string;
    image?: Resource;
  };
  isGuest?: boolean;
}

export function HeroSection({
  heroImage,
  hero,
  isGuest = true,
}: HeroSectionProps) {
  // Check if hero is just an image (no title or text)
  const isHeroJustImage = heroImage && !hero?.title && !hero?.text;

  // Use hero object if available, otherwise fall back to defaults
  const title = hero?.title || trans("auth.welcomeToLearning");
  const text = hero?.text || trans("auth.heroDescription");
  const sideImage = hero?.image;

  // RTL-aware layout
  const rtl = isRTL();
  const imageOrder = rtl ? "lg:order-2" : "lg:order-1";
  const contentOrder = rtl ? "lg:order-1" : "lg:order-2";
  const contentPadding = rtl ? "lg:pr-12" : "lg:pl-12";

  return (
    <>
      {heroImage ? (
        <div className="relative w-full overflow-hidden rounded-2xl">
          {isHeroJustImage && isGuest ? (
            <BaseLink to={URLS.auth.register} className="block">
              <img
                src={heroImage.url}
                alt="hero"
                className="transition-transform duration-300 hover:scale-105 max-w-full"
              />
              {/* Overlay with call-to-action */}
              {/* <div className="absolute inset-0 flex items-center justify-center bg-black/20">
                <div className="px-4 text-center text-white">
                  <h2 className="mb-3 text-2xl font-bold sm:text-3xl md:text-4xl">
                    {trans("auth.heroDescription")}
                  </h2>
                  <p className="mb-4 text-base opacity-90 sm:text-lg">
                    {trans("auth.startLearningNow")}
                  </p>
                  <div className="inline-flex items-center rounded-lg bg-white/20 px-4 py-2 text-base font-semibold text-white backdrop-blur-sm transition-all hover:bg-white/30 sm:px-6 sm:py-3 sm:text-lg">
                    {trans("auth.signUpNow")}
                  </div>
                </div>
              </div> */}
            </BaseLink>
          ) : (
            <img src={heroImage.url} alt="hero" className="m-auto" />
          )}
        </div>
      ) : (
        isGuest && (
          <div className="relative overflow-hidden rounded-2xl bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm transition-colors duration-300">
            <div className="relative z-10 flex min-h-[500px] flex-col gap-8 py-8 sm:py-12 lg:flex-row lg:items-center">
              {/* Image Section - Left side in LTR, Right side in RTL */}
              {sideImage && (
                <div className={`order-1 flex-shrink-0 ${imageOrder} lg:w-2/5 lg:max-w-md`}>
                  <div className="relative mx-auto flex justify-center lg:justify-start">
                    <img
                      src={sideImage.url}
                      alt="Learning experience"
                      className="h-64 w-auto max-w-full object-contain sm:h-80 xl:h-[500px]"
                      style={{ maxWidth: "min(350px, 100%)" }}
                    />
                    {/* Floating elements for visual interest */}
                    <div className="absolute -right-2 -top-2 z-20 rounded-lg bg-white dark:bg-gray-800 p-2 shadow-lg dark:shadow-xl transition-colors duration-300 sm:-right-4 sm:-top-4 sm:p-3">
                      <div className="text-lg sm:text-2xl">📚</div>
                    </div>
                    <div className="absolute -bottom-2 -left-2 z-20 rounded-lg bg-white dark:bg-gray-800 p-2 shadow-lg dark:shadow-xl transition-colors duration-300 sm:-bottom-4 sm:-left-4 sm:p-3">
                      <div className="text-lg sm:text-2xl">🎓</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Content Section - Right side in LTR, Left side in RTL */}
              <div className={`order-2 flex-1 space-y-6 px-4 sm:space-y-8 sm:px-6 ${contentOrder} lg:min-w-0 lg:flex-1 lg:px-8 ${contentPadding}`}>
                <div className="space-y-3 sm:space-y-4">
                  <h1 className="text-3xl font-bold leading-tight text-gray-900 dark:text-gray-100 sm:text-4xl lg:text-5xl xl:text-6xl">
                    {title}
                  </h1>
                  <p className="text-base leading-relaxed text-gray-600 dark:text-gray-300 sm:text-lg lg:text-xl">
                    <TypewriterText text={text} />
                  </p>
                </div>

                <div className="flex flex-col gap-3 sm:flex-row sm:gap-4 lg:gap-6">
                  <BaseLink
                    to={URLS.auth.register}
                    className="inline-flex items-center justify-center rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary-400 px-6 py-3 text-base font-semibold sm:px-8 sm:py-4 sm:text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
                  >
                    {trans("auth.startLearningNow")}
                  </BaseLink>
                  <BaseLink
                    to={URLS.auth.login}
                    className="inline-flex items-center justify-center rounded-lg border border-primary dark:border-white px-6 py-3 text-base font-semibold text-primary dark:text-white hover:bg-primary/10 dark:hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-primary-400 sm:px-8 sm:py-4 sm:text-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
                  >
                    {trans("auth.login")}
                  </BaseLink>
                </div>

                <div className="flex flex-col gap-4 text-sm text-gray-500 dark:text-gray-400 sm:flex-row sm:gap-8 sm:text-base">
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-success-500 dark:bg-success-400"></div>
                    <span>{trans("auth.qualityEducation")}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-2 w-2 rounded-full bg-success-500 dark:bg-success-400"></div>
                    <span>{trans("auth.flexibleLearning")}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )
      )}
    </>
  );
}
